import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { api, formatError } from "@/lib/api";

const Contato = () => {
  const { settings } = useSettings();
  const c = settings?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Mensagem enviada! Entraremos em contato em breve.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Erro ao enviar mensagem");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contato-page">
      <section className="bg-gradient-to-br from-primary via-primary to-primary/85 text-primary-foreground pt-28 md:pt-32 pb-12 md:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-30" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-accent blob" />
        <div className="container-pad relative">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="w-8 h-px bg-accent" />
            <span className="text-[11px] tracking-[0.3em] text-accent font-semibold uppercase">CONTATO</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-light leading-tight text-balance">Fale conosco</h1>
          <p className="mt-3 text-primary-foreground/75 max-w-2xl text-sm md:text-base">Envie sua mensagem ou agende sua consulta. Retornamos o mais breve possível.</p>
        </div>
      </section>

      <section className="container-pad py-14 md:py-16 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <div className="p-5 bg-white border border-border rounded-2xl flex items-start gap-4 hover:border-accent transition">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-accent to-emerald-600 text-accent-foreground flex items-center justify-center shrink-0"><Phone className="w-5 h-5" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Telefone / WhatsApp</div>
              <a href={`https://wa.me/${c.whatsapp}`} target="_blank" rel="noreferrer" className="font-display text-base md:text-lg text-primary hover:text-accent font-semibold" data-testid="contact-phone">{c.phone}</a>
            </div>
          </div>
          <div className="p-5 bg-white border border-border rounded-2xl flex items-start gap-4 hover:border-accent transition">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-accent to-emerald-600 text-accent-foreground flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">E-mail</div>
              <a href={`mailto:${c.email}`} className="font-display text-base md:text-lg text-primary hover:text-accent font-semibold break-all" data-testid="contact-email">{c.email}</a>
            </div>
          </div>
          <div className="p-5 bg-white border border-border rounded-2xl flex items-start gap-4 hover:border-accent transition">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-accent to-emerald-600 text-accent-foreground flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Endereço</div>
              <div className="font-display text-sm md:text-base text-primary leading-snug font-semibold">{c.address}</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-white border border-border rounded-3xl p-6 md:p-8 space-y-4 ring-medical" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Nome *</label>
              <input value={form.name} onChange={handle("name")} required className="w-full mt-1 px-4 py-3 bg-secondary/30 border border-transparent focus:border-accent rounded-xl outline-none text-sm" data-testid="contact-name" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">E-mail *</label>
              <input type="email" value={form.email} onChange={handle("email")} required className="w-full mt-1 px-4 py-3 bg-secondary/30 border border-transparent focus:border-accent rounded-xl outline-none text-sm" data-testid="contact-email-input" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Telefone</label>
              <input value={form.phone} onChange={handle("phone")} className="w-full mt-1 px-4 py-3 bg-secondary/30 border border-transparent focus:border-accent rounded-xl outline-none text-sm" data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Assunto</label>
              <input value={form.subject} onChange={handle("subject")} className="w-full mt-1 px-4 py-3 bg-secondary/30 border border-transparent focus:border-accent rounded-xl outline-none text-sm" data-testid="contact-subject" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Mensagem *</label>
            <textarea value={form.message} onChange={handle("message")} required rows={5} className="w-full mt-1 px-4 py-3 bg-secondary/30 border border-transparent focus:border-accent rounded-xl outline-none resize-none text-sm" data-testid="contact-message" />
          </div>
          <button type="submit" disabled={loading} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-primary to-accent text-primary-foreground font-medium hover:-translate-y-0.5 transition-all disabled:opacity-60 text-sm shadow-md" data-testid="contact-submit">
            {loading ? "Enviando..." : (<>Enviar mensagem <Send className="w-4 h-4" /></>)}
          </button>
        </form>
      </section>
    </div>
  );
};

export default Contato;
